"use client"

import type { MembersHomeVisitor } from "@ibada/shared-types/overview"

import { HomeTile, HomeTileEmpty, type HomeTileLink } from "@/features/dashboard/home-d4/components/home-tile"
import { PersonContactButtons } from "@/features/dashboard/home-d4/components/person-contact-buttons"
import { fullName } from "@/features/dashboard/home-d4/lib/home-format"
import { MemberAvatar } from "@/features/members/components/members-views"
import { useLocale, useTranslations } from "@/i18n"

/** Four rows fit the tile at its desktop height. */
const MAX_ROWS = 4

/**
 * Recent visitors (D4): the latest people who came, when they arrived, and a call or WhatsApp to follow up.
 * There is no attendance model, so nothing here pretends to count visits.
 */
export function RecentVisitorsTile({
  visitors,
  total,
  visitorsLink,
  addVisitor,
}: {
  visitors: MembersHomeVisitor[]
  total: number
  visitorsLink: HomeTileLink | null
  addVisitor: HomeTileLink | null
}) {
  const t = useTranslations("dashboard.home.visitors")
  const locale = useLocale()
  const dateFormat = new Intl.DateTimeFormat(locale, { day: "numeric", month: "short" })

  return (
    <HomeTile
      title={t("title")}
      subtitle={t("subtitle")}
      link={visitorsLink && total > 0 ? { ...visitorsLink, label: t("all", { count: total }) } : visitorsLink}
      testId='home-tile-visitors'
    >
      {visitors.length === 0 ? (
        <HomeTileEmpty message={t("empty")} action={addVisitor} />
      ) : (
        <ul className='flex flex-col gap-2.5'>
          {visitors.slice(0, MAX_ROWS).map((visitor) => {
            const name = fullName(visitor.firstName, visitor.lastName)
            return (
              <li key={visitor.id} data-testid='visitor-row' className='flex items-center gap-2.5'>
                <MemberAvatar member={{ name, avatarUrl: visitor.avatarUrl }} className='size-[30px] text-[11px]' />
                <span className='flex min-w-0 flex-1 flex-col'>
                  <span className='truncate text-[13px] font-bold'>{name}</span>
                  <span className='truncate text-[11.5px] text-muted-foreground'>
                    {t("arrived", { date: dateFormat.format(new Date(visitor.arrivedAt)) })}
                  </span>
                </span>
                <PersonContactButtons name={name} phone={visitor.phone} />
              </li>
            )
          })}
        </ul>
      )}
    </HomeTile>
  )
}
